import emailSpellChecker from '@zootools/email-spell-checker';
import { parse } from 'tldts';
import { z } from 'zod';

export const EMAIL_FORMAT_ERROR_MESSAGE = 'Please enter a valid email address';
export const EMAIL_TLD_ERROR_MESSAGE =
  "That email doesn't look right — check the part after the dot";

export type EmailSuggestion = {
  address: string;
  domain: string;
  full: string;
};

export type EmailSubmitDecision =
  | {
      type: 'invalid';
    }
  | {
      type: 'show_suggestion';
      normalizedEmail: string;
      suggestion: EmailSuggestion;
    }
  | {
      type: 'submit';
      normalizedEmail: string;
    };

export type EmailBlurSuggestionDecision =
  | {
      type: 'none';
    }
  | {
      type: 'show_suggestion';
      normalizedEmail: string;
      suggestion: EmailSuggestion;
    };

function hasValidPublicSuffix(email: string): boolean {
  const result = parse(email);
  return result.domain !== null && result.publicSuffix !== null && result.isIcann === true;
}

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email(EMAIL_FORMAT_ERROR_MESSAGE)
  .refine(hasValidPublicSuffix, {
    message: EMAIL_TLD_ERROR_MESSAGE,
  });

const emailFormatSchema = z.string().trim().toLowerCase().email(EMAIL_FORMAT_ERROR_MESSAGE);

export function normalizeValidEmail(email: string): string | null {
  const result = emailSchema.safeParse(email);
  return result.success ? result.data : null;
}

export function getEmailSubmitDecision(
  email: string,
  acknowledgedSuggestionForEmail: string | null,
): EmailSubmitDecision {
  const formatResult = emailFormatSchema.safeParse(email);
  if (!formatResult.success) {
    return { type: 'invalid' };
  }

  const normalizedEmail = formatResult.data;
  const suggestion = getEmailSuggestion(normalizedEmail);
  const hasSuggestion = suggestion !== null && suggestion.full !== normalizedEmail;

  if (hasSuggestion && acknowledgedSuggestionForEmail !== normalizedEmail) {
    return {
      type: 'show_suggestion',
      normalizedEmail,
      suggestion,
    };
  }

  const strictValidEmail = normalizeValidEmail(normalizedEmail);
  if (!strictValidEmail) {
    return { type: 'invalid' };
  }

  return {
    type: 'submit',
    normalizedEmail: strictValidEmail,
  };
}

export function getEmailBlurSuggestionDecision(
  email: string,
  acknowledgedSuggestionForEmail: string | null,
): EmailBlurSuggestionDecision {
  const formatResult = emailFormatSchema.safeParse(email);
  if (!formatResult.success) {
    return { type: 'none' };
  }

  const normalizedEmail = formatResult.data;
  if (acknowledgedSuggestionForEmail === normalizedEmail) {
    return { type: 'none' };
  }

  const suggestion = getEmailSuggestion(normalizedEmail);
  const hasSuggestion = suggestion !== null && suggestion.full !== normalizedEmail;
  if (!hasSuggestion) {
    return { type: 'none' };
  }

  return {
    type: 'show_suggestion',
    normalizedEmail,
    suggestion,
  };
}

export function getEmailSuggestion(email: string): EmailSuggestion | null {
  const normalisedEmail = email.trim().toLowerCase();

  if (!normalisedEmail) {
    return null;
  }

  const suggestion = emailSpellChecker.run({ email: normalisedEmail });

  return suggestion ? { ...suggestion } : null;
}
